import type { MediaAttachment } from '../models/attachment';

// Sizing constants for MediaLayout container bounds
export const MEDIA_LAYOUT_CONSTANTS = {
  comfortable: {
    single: { maxHeight: 400, maxWidth: 500 },
    multi: { height: 300, maxWidth: 500 }
  },
  compact: {
    single: { maxHeight: 250, maxWidth: 400 },
    multi: { height: 200, maxWidth: 400 }
  },
  aspectRatioBounds: { min: 0.4, max: 2.5 }
} as const;

export type AspectRatioType = 'portrait' | 'square' | 'landscape' | 'extreme-portrait' | 'extreme-landscape';

export interface ImageClassification {
  attachment: MediaAttachment;
  aspectRatio: number;
  type: AspectRatioType;
  isExtreme: boolean;
}

/**
 * Calculate aspect ratio from width and height
 */
export const getAspectRatio = (width?: number, height?: number): number => {
  if (!width || !height || width <= 0 || height <= 0) {
    return 1; // Default to square if dimensions are missing
  }
  return width / height;
};

/**
 * Clamp aspect ratio to prevent extreme layouts
 */
export const clampAspectRatio = (ratio: number): number => {
  const { min, max } = MEDIA_LAYOUT_CONSTANTS.aspectRatioBounds;
  return Math.max(min, Math.min(max, ratio));
};

/**
 * Calculate bounded dimensions for single image display
 * Returns dimensions that fit within maxWidth/maxHeight while preserving clamped ratio
 */
export const getSingleImageDimensions = (
  width: number | undefined,
  height: number | undefined,
  mode: 'compact' | 'comfortable' = 'comfortable'
): { width: number; height: number } => {
  const config = MEDIA_LAYOUT_CONSTANTS[mode].single;
  const rawRatio = getAspectRatio(width, height);
  const ratio = clampAspectRatio(rawRatio);

  // Start with max dimensions
  let finalW = config.maxWidth;
  let finalH = finalW / ratio;

  // If too tall, constrain by height
  if (finalH > config.maxHeight) {
    finalH = config.maxHeight;
    finalW = finalH * ratio;
  }

  return { width: Math.round(finalW), height: Math.round(finalH) };
};

/**
 * Classify image based on aspect ratio
 */
export const classifyImage = (aspectRatio: number): AspectRatioType => {
  if (aspectRatio < 0.5) return 'extreme-portrait';
  if (aspectRatio < 0.8) return 'portrait';
  if (aspectRatio <= 1.2) return 'square';
  if (aspectRatio <= 2.0) return 'landscape';
  return 'extreme-landscape';
};

/**
 * Check if aspect ratio is extreme
 */
export const isExtremeRatio = (aspectRatio: number): boolean => {
  return aspectRatio < 0.5 || aspectRatio > 2.0;
};

/**
 * Check if all images are similar portrait ratios (like 9:16)
 */
export const areAllSimilarPortrait = (classified: ImageClassification[]): boolean => {
  return classified.every((c) => {
    const ratio = c.aspectRatio;
    // Portrait ratios between 0.5 and 0.7 (similar to 9:16 ≈ 0.56)
    return ratio >= 0.4 && ratio <= 0.7;
  });
};

/**
 * Check if all images are similar landscape ratios (like 16:9)
 */
export const areAllSimilarLandscape = (classified: ImageClassification[]): boolean => {
  return classified.every((c) => {
    const ratio = c.aspectRatio;
    // Landscape ratios between 1.4 and 1.8 (similar to 16:9 ≈ 1.78)
    return ratio >= 1.4 && ratio <= 1.8;
  });
};

/**
 * Check for special case: 1 wide landscape + 2 tall portraits
 */
export const isWideLandscapeWithTallPortraits = (classified: ImageClassification[]): boolean => {
  if (classified.length !== 3) return false;

  const first = classified[0];
  const [second, third] = classified.slice(1);

  // First image is very wide landscape (16:4 ≈ 4.0 or similar)
  const isFirstVeryWide = first.aspectRatio >= 3.0;

  // Second and third are very tall portraits (4:16 ≈ 0.25 or similar)
  const areSecondThirdVeryTall = second.aspectRatio <= 0.4 && third.aspectRatio <= 0.4;
  return isFirstVeryWide && areSecondThirdVeryTall;
};

/**
 * Check if all images are very tall portraits (like 4:16)
 */
export const areAllVeryTallPortraits = (classified: ImageClassification[]): boolean => {
  return classified.every((c) => c.aspectRatio <= 0.4);
};

/**
 * Check if all images are very wide landscapes (like 16:4)
 */
export const areAllVeryWideLandscapes = (classified: ImageClassification[]): boolean => {
  return classified.every((c) => c.aspectRatio >= 3.0);
};

/**
 * Classify a media attachment
 */
export const classifyAttachment = (attachment: MediaAttachment): ImageClassification => {
  const aspectRatio = getAspectRatio(attachment.width, attachment.height);
  const type = classifyImage(aspectRatio);
  const isExtreme = isExtremeRatio(aspectRatio);

  return {
    attachment,
    aspectRatio,
    type,
    isExtreme
  };
};

/**
 * Sort images for optimal layout
 * Priority: landscape > square > portrait
 * This ensures better grid utilization
 */
export const sortImagesForLayout = (attachments: MediaAttachment[]): MediaAttachment[] => {
  const classified = attachments.map(classifyAttachment);

  // Sort by priority: landscape (3) > square (2) > portrait (1)
  const sorted = classified.sort((a, b) => {
    const getPriority = (type: AspectRatioType): number => {
      if (type === 'extreme-landscape' || type === 'landscape') return 3;
      if (type === 'square') return 2;
      return 1; // portrait or extreme-portrait
    };

    const priorityDiff = getPriority(b.type) - getPriority(a.type);
    if (priorityDiff !== 0) return priorityDiff;

    // If same priority, prefer less extreme ratios
    return Math.abs(1 - a.aspectRatio) - Math.abs(1 - b.aspectRatio);
  });

  return sorted.map((c) => c.attachment);
};

export type LayoutType =
  | 'single'
  | 'two-horizontal'
  | 'two-vertical'
  | 'three-horizontal'
  | 'three-large-left'
  | 'three-balanced'
  | 'three-wide-top-tall-bottom'
  | 'four-grid'
  | 'four-large-left'
  | 'five-grid';

export interface LayoutConfig {
  type: LayoutType;
  containerClass: string;
  itemClasses: string[];
  objectFits: ('cover' | 'contain')[];
}

/**
 * Select optimal layout based on image classifications
 */
export const selectLayout = (attachments: MediaAttachment[]): LayoutConfig => {
  const count = attachments.length;
  const classified = attachments.map(classifyAttachment);

  if (count === 1) {
    // Always use 'contain' for single image to show full image without cropping
    return {
      type: 'single',
      containerClass: 'grid grid-cols-1',
      itemClasses: ['w-full h-full'],
      objectFits: ['contain']
    };
  }

  // For 2+ images, always use 'cover' for better grid fill
  const allCover: ('cover' | 'contain')[] = classified.map(() => 'cover');

  if (count === 2) {
    // Special case: Both very tall portraits (like 4:16) -> horizontal
    if (areAllVeryTallPortraits(classified)) {
      return {
        type: 'two-horizontal',
        containerClass: 'grid grid-cols-2 gap-0.5',
        itemClasses: ['w-full h-full', 'w-full h-full'],
        objectFits: allCover
      };
    }

    // Special case: Both very wide landscapes (like 16:4) -> vertical
    if (areAllVeryWideLandscapes(classified)) {
      return {
        type: 'two-vertical',
        containerClass: 'grid grid-cols-1 gap-0.5',
        itemClasses: ['w-full h-full', 'w-full h-full'],
        objectFits: allCover
      };
    }

    // Special case: Both similar portrait ratios (like 9:16) -> horizontal
    if (areAllSimilarPortrait(classified)) {
      return {
        type: 'two-horizontal',
        containerClass: 'grid grid-cols-2 gap-0.5',
        itemClasses: ['w-full h-full', 'w-full h-full'],
        objectFits: allCover
      };
    }

    // Check if both are portrait or both are landscape
    const bothPortrait = classified.every((c) => c.type.includes('portrait'));
    const bothLandscape = classified.every((c) => c.type.includes('landscape'));

    if (bothPortrait) {
      // Portrait images work better side by side
      return {
        type: 'two-horizontal',
        containerClass: 'grid grid-cols-2 gap-0.5',
        itemClasses: ['w-full h-full', 'w-full h-full'],
        objectFits: allCover
      };
    }

    if (bothLandscape) {
      // Landscape images work better stacked vertically
      return {
        type: 'two-vertical',
        containerClass: 'grid grid-cols-1 gap-0.5',
        itemClasses: ['w-full h-full', 'w-full h-full'],
        objectFits: allCover
      };
    }

    // Mixed: horizontal layout
    return {
      type: 'two-horizontal',
      containerClass: 'grid grid-cols-2 gap-0.5',
      itemClasses: ['w-full h-full', 'w-full h-full'],
      objectFits: allCover
    };
  }

  if (count === 3) {
    // Special case: 1 wide landscape + 2 tall portraits (16:4 + 2x 4:16)
    if (isWideLandscapeWithTallPortraits(classified)) {
      return {
        type: 'three-wide-top-tall-bottom',
        containerClass: 'grid grid-cols-2 grid-rows-2 gap-0.5',
        itemClasses: [
          'col-span-2 row-span-1 w-full h-full', // Wide landscape on top
          'col-span-1 row-span-1 w-full h-full', // First tall portrait bottom left
          'col-span-1 row-span-1 w-full h-full' // Second tall portrait bottom right
        ],
        objectFits: allCover
      };
    }
    // Special case: All similar portrait ratios (like 9:16) -> horizontal layout
    if (areAllSimilarPortrait(classified)) {
      return {
        type: 'three-horizontal',
        containerClass: 'grid grid-cols-2 grid-rows-2 gap-0.5 grid-cols-[auto_auto]',
        itemClasses: ['col-span-1 row-span-2', 'col-span-1 row-span-1', 'col-span-1 row-span-1'],
        objectFits: allCover
      };
    }

    // Special case: All very tall portraits (like 4:16) -> horizontal layout
    if (areAllVeryTallPortraits(classified)) {
      return {
        type: 'three-horizontal',
        containerClass: 'grid grid-cols-1 grid-rows-3 gap-0.5',
        itemClasses: [
          'col-span-1 row-span-1 w-full h-full',
          'col-span-1 row-span-1 w-full h-full',
          'col-span-1 row-span-1 w-full h-full'
        ],
        objectFits: allCover
      };
    }

    const firstIsLandscape = classified[0].type.includes('landscape');
    if (firstIsLandscape) {
      // Large landscape on left, 2 smaller on right
      return {
        type: 'three-large-left',
        containerClass: 'grid grid-rows-2 grid-cols-[auto_auto] gap-0.5 grid-rows-[auto_auto]',
        itemClasses: ['col-span-2 row-span-1', 'col-span-1 row-span-1', 'col-span-1 row-span-1'],
        objectFits: allCover
      };
    }

    // Balanced layout: 2 on top, 1 on bottom
    return {
      type: 'three-balanced',
      containerClass: 'grid grid-cols-2 grid-rows-2 gap-0.5',
      itemClasses: [
        'col-span-1 row-span-1 w-full h-full',
        'col-span-1 row-span-1 w-full h-full',
        'col-span-2 row-span-1 w-full h-full'
      ],
      objectFits: allCover
    };
  }

  // 4 or more images
  if (count === 4) {
    // Special case: All very tall portraits (like 4:16) -> horizontal row
    if (areAllVeryTallPortraits(classified)) {
      return {
        type: 'four-grid',
        containerClass: 'grid grid-cols-4 gap-0.5',
        itemClasses: [
          'col-span-1 w-full h-full',
          'col-span-1 w-full h-full',
          'col-span-1 w-full h-full',
          'col-span-1 w-full h-full'
        ],
        objectFits: allCover
      };
    }

    // Special case: All similar portrait ratios (like 9:16) -> 2x2 grid
    if (areAllSimilarPortrait(classified)) {
      return {
        type: 'four-grid',
        containerClass: 'grid grid-cols-2 grid-rows-2 gap-0.5',
        itemClasses: [
          'col-span-1 row-span-1 w-full h-full',
          'col-span-1 row-span-1 w-full h-full',
          'col-span-1 row-span-1 w-full h-full',
          'col-span-1 row-span-1 w-full h-full'
        ],
        objectFits: allCover
      };
    }

    const firstIsLandscape = classified[0]?.type.includes('landscape');

    if (firstIsLandscape) {
      // Large landscape on left, 3 smaller on right (stacked)
      return {
        type: 'four-large-left',
        containerClass: 'grid grid-cols-2 grid-rows-3 gap-0.5 grid-cols-[auto_auto] grid-rows-[auto_auto_auto]',
        itemClasses: [
          'col-span-1 row-span-3 w-full h-full',
          'col-span-1 row-span-1',
          'col-span-1 row-span-1',
          'col-span-1 row-span-1'
        ],
        objectFits: allCover
      };
    }

    // Default 2x2 grid for 4 images
    return {
      type: 'four-grid',
      containerClass: 'grid grid-cols-2 grid-rows-2 gap-0.5',
      itemClasses: [
        'col-span-1 row-span-1 w-full h-full',
        'col-span-1 row-span-1 w-full h-full',
        'col-span-1 row-span-1 w-full h-full',
        'col-span-1 row-span-1 w-full h-full'
      ],
      objectFits: allCover
    };
  }

  // 5 images: 2 on top + 3 on bottom
  if (count === 5) {
    return {
      type: 'five-grid',
      containerClass: 'grid grid-cols-6 grid-rows-2 gap-0.5',
      itemClasses: [
        'col-span-3 row-span-1 w-full h-full',
        'col-span-3 row-span-1 w-full h-full',
        'col-span-2 row-span-1 w-full h-full',
        'col-span-2 row-span-1 w-full h-full',
        'col-span-2 row-span-1 w-full h-full'
      ],
      objectFits: allCover
    };
  }

  // Fallback for 6+ images (should not reach here due to slice(0,5) in MediaLayout)
  return {
    type: 'four-grid',
    containerClass: 'grid grid-cols-2 grid-rows-2 gap-0.5',
    itemClasses: [
      'col-span-1 row-span-1 w-full h-full',
      'col-span-1 row-span-1 w-full h-full',
      'col-span-1 row-span-1 w-full h-full',
      'col-span-1 row-span-1 w-full h-full'
    ],
    objectFits: allCover.slice(0, 4)
  };
};
